import { useEffect, useRef, useState } from "react";
import PowerButton from "./components/PowerButton";
import WaveVisual from "./components/WaveVisual";
import ModeSelector from "./components/ModeSelector";
import IntensitySlider from "./components/IntensitySlider";
import FeedbackPanel from "./components/FeedbackPanel";
import ConnectionPanel from "./components/ConnectionPanel";
import HeatingControl from "./components/HeatingControl";
import SprayButton from "./components/SprayButton";
import useDeviceHistory from "@/hooks/useDeviceHistory";
import { DEVICE_MODES } from "@/mocks/deviceModes";
import { REMOTE_DEVICES, type RemoteDevice } from "@/mocks/device";

export default function Home() {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [deviceCode, setDeviceCode] = useState("");
  const [connectError, setConnectError] = useState("");
  const [currentDevice, setCurrentDevice] = useState<RemoteDevice | null>(null);
  const { history, saveDevice, removeDevice, clearHistory } = useDeviceHistory();

  const [isOn, setIsOn] = useState(false);
  const [modeId, setModeId] = useState(DEVICE_MODES[0].id);
  const [intensity, setIntensity] = useState(40);
  const [seconds, setSeconds] = useState(0);
  const [battery, setBattery] = useState(50);
  const [temp, setTemp] = useState(32);
  const [heating, setHeating] = useState(false);
  const [heatTarget, setHeatTarget] = useState(42);
  const [spraying, setSpraying] = useState(false);
  const batteryTickRef = useRef(0);

  useEffect(() => {
    if (!isOn || !connected) return;
    const t = window.setInterval(() => {
      setSeconds((s) => s + 1);
      setTemp((tmp) => {
        const base = 32 + intensity * 0.09;
        const target = heating ? heatTarget : base;
        return +(tmp + (target - tmp) * 0.04).toFixed(1);
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [isOn, intensity, connected, heating, heatTarget]);

  useEffect(() => {
    if (!isOn || !connected) return;
    batteryTickRef.current = 0;
    const t = window.setInterval(() => {
      batteryTickRef.current += 1;
      if (batteryTickRef.current >= 30) {
        batteryTickRef.current = 0;
        setBattery((b) => Math.max(0, b - 1));
      }
    }, 1000);
    return () => window.clearInterval(t);
  }, [isOn, connected]);

  const handleCodeChange = (v: string) => {
    setDeviceCode(v.replace(/\D/g, "").slice(0, 6));
    if (connectError) setConnectError("");
  };

  const connectTo = (codeToConnect: string) => {
    if (codeToConnect.length !== 6 || connecting) return;
    setConnectError("");
    setConnecting(true);
    window.setTimeout(() => {
      const found = REMOTE_DEVICES.find((d) => d.code === codeToConnect);
      setConnecting(false);
      if (found) {
        setCurrentDevice(found);
        setConnected(true);
        saveDevice(found);
      } else {
        setConnectError("デバイスが見つかりません。コードを確認してください");
      }
    }, 1200);
  };

  const handleConnect = () => connectTo(deviceCode);

  const handleReconnect = (device: RemoteDevice) => {
    setDeviceCode(device.code);
    connectTo(device.code);
  };

  const handleDisconnect = () => {
    setConnected(false);
    setDeviceCode("");
    setCurrentDevice(null);
    setIsOn(false);
    setSeconds(0);
    setBattery(50);
    setTemp(32);
    setHeating(false);
    setHeatTarget(42);
    setSpraying(false);
  };

  const handleSpray = () => {
    if (spraying || !isOn) return;
    setSpraying(true);
    window.setTimeout(() => setSpraying(false), 1500);
  };

  const activeMode = DEVICE_MODES.find((m) => m.id === modeId) ?? DEVICE_MODES[0];
  const fmt = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const homeIndicator = (
    <div className="flex shrink-0 justify-center pb-3 pt-2">
      <span className="h-1 w-32 rounded-full bg-foreground-900/80" />
    </div>
  );

  const screenContent = connected && currentDevice ? (
    <div className="px-6 pb-5 pt-4">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-wide text-foreground-950">
            AURA
          </h1>
          <p className="text-xs text-foreground-500">
            {currentDevice.name} · {currentDevice.model}
          </p>
        </div>
        <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
          <i className="ri-bluetooth-connect-line text-primary-600" /> 接続済み
        </span>
      </header>

      {/* Visualization */}
      <WaveVisual active={isOn} intensity={intensity} />

      {/* Power · presets · intensity — one row, spread apart */}
      <div className="flex items-center justify-between">
        <PowerButton isOn={isOn} onToggle={() => setIsOn((v) => !v)} />

        <div className="flex flex-col items-center gap-2">
          {[100, 75, 50, 25].map((p, i) => {
            const active = intensity === p;
            const size = [
              "h-8 w-16 text-sm",
              "h-7 w-14 text-xs",
              "h-7 w-12 text-xs",
              "h-6 w-10 text-[11px]",
            ][i];
            return (
              <div key={p} className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setIntensity(p)}
                  className={`cursor-pointer whitespace-nowrap rounded-full font-medium transition-colors ${size} ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-200 bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {p}%
                </button>
                {/* 当前档位连线 · 指向强度条 */}
                <span className="flex w-5 items-center">
                  <span
                    className={`h-px w-3.5 bg-gradient-to-r from-primary-500 to-primary-300 transition-opacity duration-200 ${
                      active ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <span
                    className={`ml-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500 ${
                      active ? "animate-pulse opacity-100" : "opacity-0"
                    }`}
                  />
                </span>
              </div>
            );
          })}
        </div>

        <IntensitySlider value={intensity} onChange={setIntensity} />
      </div>

      {/* Modes */}
      <ModeSelector
        modes={DEVICE_MODES}
        activeId={modeId}
        onSelect={setModeId}
      />

      {/* Features: heating & spray */}
      <div className="mt-3">
        <p className="mb-1.5 text-sm font-medium text-foreground-800">機能</p>
        <div className="grid grid-cols-2 items-start gap-2">
          <HeatingControl
            heating={heating}
            isOn={isOn}
            tempLevel={heatTarget}
            onToggle={() => setHeating((v) => !v)}
            onTempLevelChange={setHeatTarget}
          />
          <SprayButton isOn={isOn} spraying={spraying} onSpray={handleSpray} />
        </div>
      </div>

      <FeedbackPanel
        battery={battery}
        temp={temp}
        seconds={fmt(seconds)}
        isOn={isOn}
        intensity={intensity}
        modeName={activeMode.name}
        heating={heating}
        heatTarget={heatTarget}
        spraying={spraying}
      />

      {/* Disconnect */}
      <button
        type="button"
        onClick={handleDisconnect}
        className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-200 bg-background-100 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-200"
      >
        <i className="ri-link-unlink" /> 切断する
      </button>
    </div>
  ) : (
    <ConnectionPanel
      code={deviceCode}
      onCodeChange={handleCodeChange}
      onConnect={handleConnect}
      connecting={connecting}
      error={connectError}
      history={history}
      onReconnect={handleReconnect}
      onRemoveDevice={removeDevice}
      onClearHistory={clearHistory}
    />
  );

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-background-50">
      {/* Ambient background — desktop only */}
      <div className="pointer-events-none absolute inset-0 hidden sm:block">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20abstract%20flowing%20silk%20waves%20in%20soft%20rose%20gold%20and%20champagne%20amber%20tones%2C%20elegant%20smooth%20curves%2C%20luxurious%20premium%20minimal%20aesthetic%2C%20soft%20diffused%20lighting%2C%20serene%20calm%20atmosphere%2C%20artistic%20high%20resolution%20background&width=1920&height=1080&seq=control-ambient-01&orientation=landscape"
          alt="柔らかな抽象的なシルクの質感の背景"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/50 via-background-50/20 to-background-50" />
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-primary-200/40 blur-3xl" />
        <div className="absolute -right-20 bottom-10 h-80 w-80 rounded-full bg-accent-200/40 blur-3xl" />
        <div className="absolute left-1/2 top-1/2 h-[72vh] w-[72vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-100/45 blur-[120px]" />
        <div className="absolute left-[12%] top-[18%] h-40 w-40 rounded-full bg-accent-100/50 blur-3xl" />
        <div className="absolute right-[10%] top-[30%] h-24 w-24 rounded-full bg-secondary-100/60 blur-2xl" />
        <div className="absolute bottom-[16%] right-[22%] h-16 w-16 rounded-full bg-primary-100/50 blur-2xl" />
      </div>

      {/* App shell */}
      <main className="relative z-10 mx-auto flex h-full w-full max-w-[420px] items-stretch justify-center sm:py-8">
        <div className="relative flex h-full w-full flex-col">
          {/* 落影 — 手机壳落在桌面的柔和投影 */}
          <div className="pointer-events-none absolute -bottom-3 left-1/2 h-14 w-[78%] -translate-x-1/2 rounded-[100%] bg-foreground-900/25 blur-2xl" />
          {/* 手机壳后方的柔光描边 */}
          <div className="pointer-events-none absolute -inset-3 rounded-[2.6rem] bg-gradient-to-b from-primary-100/50 via-transparent to-accent-100/40 blur-2xl" />
          <div className="app-shell relative flex h-full w-full flex-col overflow-hidden sm:rounded-[2rem] sm:border sm:border-background-200/60 sm:bg-background-50/85 sm:backdrop-blur-xl">
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div key={connected ? "control" : "connect"} className="screen-enter">
                {screenContent}
              </div>
            </div>
            {homeIndicator}
          </div>
        </div>
      </main>
    </div>
  );
}